import { useRender } from '@base-ui/react/use-render'
import { type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils.ts'

import { badgeVariants } from './badge.variants.ts'

/**
 * Maps the semantic `intent` axis onto the raw `color` axis (the single visual source of truth).
 * The two grayscale Figma variants both get a semantic name: `base` → the filled `gray` chip
 * (the default, matching pharaoh v1), `neutral` → the lighter outlined `neutral` chip.
 */
const INTENT_TO_COLOR = {
  base: 'gray',
  neutral: 'neutral',
  info: 'blue',
  success: 'green',
  warning: 'orange',
  danger: 'red',
} as const

/**
 * Badge is a small, non-interactive status or label indicator.
 *
 * Choose a color two ways: the semantic `intent`
 * (`base`/`neutral`/`info`/`success`/`warning`/`danger`) for meaning-carrying status, or the raw
 * `color` palette (`gray`/`blue`/…/`peach`) for categorical or decorative use that carries no
 * semantic meaning. When both are set, `color` wins. `emphasis` switches between the pale `subtle`
 * look and the saturated `solid` look. Defaults to a `<span>`; pass `render` (e.g.
 * `render={<output />}`) to change the element.
 *
 * **Accessibility** — no implicit `role`. Add `role="status"` / `aria-live` only for genuinely
 * dynamic status. Icon-only badges must pass an `aria-label`.
 *
 * @summary Status/label indicator: semantic `intent` + full-palette `color` escape hatch × `emphasis`.
 */
function Badge(props: Badge.Props) {
  const { className, emphasis = 'subtle', intent, color, render, ref, ...rest } = props
  // `color` (raw palette) wins; otherwise the semantic `intent` maps to a color, defaulting to `base` → `gray`.
  const resolvedColor = color ?? INTENT_TO_COLOR[intent ?? 'base']
  return useRender({
    render,
    ref,
    defaultTagName: 'span',
    props: {
      'data-slot': 'badge',
      className: cn(badgeVariants({ color: resolvedColor, emphasis, className })),
      ...rest,
    },
  })
}

namespace Badge {
  type Variants = NonNullable<VariantProps<typeof badgeVariants>>
  export type Props = useRender.ComponentProps<'span'> & {
    /** Semantic color family. Ignored when `color` is set. @default 'base' */
    intent?: 'base' | 'neutral' | 'info' | 'success' | 'warning' | 'danger'
    /** Raw palette color (escape hatch); takes precedence over `intent`. */
    color?: NonNullable<Variants['color']> | undefined
    /** Visual emphasis: pale `subtle` or saturated `solid`. @default 'subtle' */
    emphasis?: NonNullable<Variants['emphasis']> | undefined
  }
}

export { Badge, badgeVariants }
